import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDownIcon,
  Info,
  Plus,
  RefreshCcw,
  Search,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store';
import { selectAuthUser, selectRoles } from 'store/slices/auth/auth.selector';
import { useProjectsQuery } from 'service/project/hooks/useProjectsQuery';
import {
  useConvertToScrumCheckQuery,
  useConvertToScrumMutation,
} from 'service/pmo/hooks/useConvertToScrum';
import { useDebounce } from 'hooks/useDebounce';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import PmoConvertToScrumModal, {
  ConvertToScrumStage,
} from 'components/Modals/PmoConvertToScrumModal';
import { showToast } from 'utils/toast';
import { Project } from 'types/project';

/** Who may convert a project. PM Managers and PM Users can't. */
const CONVERT_ROLES = ['ORG_OWNER', 'ORG_ADMIN', 'PMO_ADMIN'];

/**
 * How long the converting state shows before it gives way to "taking longer
 * than usual". The server's own limit is 60 seconds; the request carries on.
 */
const SLOW_CONVERSION_MS = 30_000;

// Letter-tile colours for projects without an icon, picked by name so a
// project keeps its colour from one visit to the next.
const TILE_COLORS = ['#2563EB', '#EA7A2B', '#7C3AED', '#059669', '#DB2777', '#0891B2'];

const tileColor = (name: string) =>
  TILE_COLORS[
    name.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % TILE_COLORS.length
  ];

/** A board type of null or unknown is left to the check endpoint to rule on. */
const isScrum = (project: Pick<Project, 'boardType'>) =>
  Boolean(project.boardType) && project.boardType !== 'KANBAN';

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const ProjectTile = ({
  name,
  iconURL,
  size = 'md',
  muted = false,
}: {
  name: string;
  iconURL?: string | null;
  size?: 'sm' | 'md' | 'lg';
  muted?: boolean;
}) => {
  const box = { sm: 'h-5 w-5 text-[10px]', md: 'h-7 w-7 text-xs', lg: 'h-9 w-9 text-sm' }[size];
  return (
    <span
      className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-md font-semibold text-white ${box} ${
        muted ? 'opacity-40' : ''
      }`}
      style={iconURL ? undefined : { backgroundColor: tileColor(name) }}
    >
      {iconURL ? (
        <img src={`/assets/${iconURL}.png`} alt="" className="h-full w-full object-cover" />
      ) : (
        name.charAt(0).toUpperCase()
      )}
    </span>
  );
};

interface ProjectPickerProps {
  value: Project | null;
  onChange: (project: Project) => void;
}

const ProjectPicker = ({ value, onChange }: ProjectPickerProps) => (
  <Popover className="relative block w-full max-w-[440px] text-left">
    <PopoverButton
      type="button"
      className="flex h-10 w-full items-center gap-2 rounded-lg border border-primary-subtle bg-[#161B27] px-3 text-sm text-neutral transition-colors focus:outline-none data-[open]:border-[#8B5CF6]/70"
    >
      {value ? (
        <>
          <ProjectTile name={value.name} iconURL={value.iconURL} size="sm" />
          <span className="flex-1 truncate text-left text-white">
            {value.name} · {value.projectKey}
          </span>
        </>
      ) : (
        <span className="flex-1 truncate text-left text-neutral-medium">
          Choose a Kanban project
        </span>
      )}
      <ChevronDownIcon size={16} className="flex-shrink-0 text-neutral-medium" />
    </PopoverButton>

    <PopoverPanel
      transition
      anchor="bottom start"
      className="z-[9999] mt-2 w-[var(--button-width)] rounded-lg border border-primary-subtle bg-primary-variant p-1 shadow-lg transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
    >
      {({ close }) => (
        // Mounted only while open, so the search starts empty each time.
        <ProjectOptions
          value={value}
          onSelect={(project) => {
            onChange(project);
            close();
          }}
        />
      )}
    </PopoverPanel>
  </Popover>
);

const ProjectOptions = ({
  value,
  onSelect,
}: {
  value: Project | null;
  onSelect: (project: Project) => void;
}) => {
  const authUser = useAppSelector(selectAuthUser);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { data, isLoading, isError } = useProjectsQuery(authUser?.appUserUuid, {
    search: debouncedSearch || undefined,
    page: 0,
    size: 50,
  });
  // Kanban first: those are the ones that can be picked.
  const projects = [...(data?.data ?? [])].sort(
    (a, b) => Number(isScrum(a)) - Number(isScrum(b)),
  );

  return (
    <>
      <div className="mb-1 flex h-9 items-center gap-2 rounded-md border border-primary-subtle bg-primary-light px-2">
        <Search size={14} className="flex-shrink-0 text-neutral-medium" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects..."
          className="min-w-0 flex-1 bg-transparent text-sm text-neutral outline-none placeholder:text-neutral-medium"
        />
      </div>
      <div className="custom-scrollbar max-h-64 overflow-y-auto">
        {isLoading ? (
          <p className="px-3 py-2 text-sm text-neutral-medium">Loading projects…</p>
        ) : isError ? (
          <p className="px-3 py-2 text-sm text-neutral-medium">Couldn't load projects.</p>
        ) : projects.length === 0 ? (
          <p className="px-3 py-2 text-sm text-neutral-medium">No projects found</p>
        ) : (
          projects.map((project) => {
            const scrum = isScrum(project);
            const selected = project.projectUuid === value?.projectUuid;
            return (
              <button
                key={project.projectUuid}
                type="button"
                disabled={scrum}
                onClick={() => onSelect(project)}
                className={`flex w-full items-center gap-3 rounded px-3 py-2 text-left transition-colors enabled:hover:bg-primary-subtle disabled:cursor-not-allowed ${
                  selected ? 'bg-primary-subtle' : ''
                }`}
              >
                <ProjectTile name={project.name} iconURL={project.iconURL} muted={scrum} />
                <span className={`flex min-w-0 flex-1 flex-col ${scrum ? 'opacity-50' : ''}`}>
                  <span className="truncate text-sm text-white">{project.name}</span>
                  <span className="text-xs uppercase text-neutral-medium">
                    {project.projectKey}
                  </span>
                </span>
                {scrum ? (
                  <span className="flex-shrink-0 text-[11px] text-[#A78BFA]/70">Already Scrum</span>
                ) : (
                  <span className="flex-shrink-0 rounded border border-primary-subtle px-1.5 py-0.5 text-[11px] text-neutral">
                    Kanban
                  </span>
                )}
                {selected && <Check size={14} className="flex-shrink-0 text-neutral-medium" />}
              </button>
            );
          })
        )}
      </div>
    </>
  );
};

const StatCard = ({
  label,
  value,
  note,
  noteClassName,
}: {
  label: string;
  value: number;
  note: string;
  noteClassName: string;
}) => (
  <div className="flex flex-col gap-1 rounded-lg border border-primary-subtle bg-primary-light p-3">
    <span className="text-xs text-neutral-medium">{label}</span>
    <span className="text-2xl font-semibold text-white">{value}</span>
    <span className={`text-xs ${noteClassName}`}>{note}</span>
  </div>
);

const ChangeItem = ({ isNew = false, children }: { isNew?: boolean; children: React.ReactNode }) => (
  <li className="flex items-start gap-3 text-sm text-neutral">
    <span
      className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full ${
        isNew ? 'bg-[#3B82F6]/20 text-[#60A5FA]' : 'bg-[#10B981]/20 text-[#34D399]'
      }`}
    >
      {isNew ? <Plus size={10} /> : <Check size={10} />}
    </span>
    <span>{children}</span>
  </li>
);

/**
 * Moves a Kanban project to Scrum: open work goes to the Backlog, done work
 * stays put. One-way — Scrum projects can't go back to Kanban.
 *
 * Org Owners, Org Admins and PMO Admins only.
 */
const ConvertToScrum = () => {
  const roles = useAppSelector(selectRoles);
  const canConvert = (roles ?? []).some((role: string) => CONVERT_ROLES.includes(role));

  // Arriving from Edit Project preselects that project.
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(
    location.state?.project ?? null,
  );
  const {
    data: check,
    isLoading: isCheckLoading,
    isError: isCheckError,
    error: checkError,
    refetch: refetchCheck,
  } = useConvertToScrumCheckQuery(project?.projectUuid);

  const { toBacklog } = usePmoNavigation();
  const convert = useConvertToScrumMutation();

  // The open flag is kept apart from the stage, so the modal fades out on
  // the stage it was showing.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stage, setStage] = useState<ConvertToScrumStage>('confirm');
  const [converted, setConverted] = useState<{ name: string; projectKey: string } | null>(null);

  const slowTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(slowTimer.current), []);

  const openConfirm = () => {
    setStage('confirm');
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    if (!check || convert.isPending) return;
    const target = { name: check.projectName, projectKey: check.projectKey };
    setStage('converting');
    slowTimer.current = setTimeout(() => setStage('slow'), SLOW_CONVERSION_MS);

    convert.mutate(check.projectUuid, {
      // Also lands after the slow dialog was closed, if the page is still open.
      onSuccess: (result) => {
        clearTimeout(slowTimer.current);
        setIsModalOpen(false);
        setProject(null);
        setConverted(target);
        showToast.success(
          result.message || 'Project converted to Scrum. Create your first Sprint from the Backlog.',
        );
      },
      onError: (error) => {
        clearTimeout(slowTimer.current);
        setIsModalOpen(false);
        showToast.error(error.message || `Couldn't convert ${target.name}. Please try again.`);
      },
    });
  };

  const renderPreview = () => {
    if (!project) {
      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-primary-subtle px-6 text-center">
          <span className="mb-1 flex h-9 w-9 items-center justify-center rounded-full bg-white/5">
            <RefreshCcw size={16} className="text-neutral-medium" />
          </span>
          <p className="text-sm text-neutral">Select a project to see what will change</p>
          <p className="max-w-[280px] text-xs text-neutral-medium">
            You'll see how many work items move to the Backlog before you confirm.
          </p>
        </div>
      );
    }

    if (isCheckLoading) {
      return (
        <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-primary-subtle text-sm text-neutral-medium">
          Checking {project.name}…
        </div>
      );
    }

    if (isCheckError || !check) {
      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-primary-subtle text-center">
          <p className="text-sm text-neutral">
            {checkError?.message || `Couldn't check ${project.name}.`}
          </p>
          <button
            type="button"
            onClick={() => refetchCheck()}
            className="rounded-lg border border-primary-subtle px-3 py-1.5 text-xs text-neutral transition-colors hover:text-white"
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-5 rounded-xl border border-primary-subtle bg-[#161B27] p-4">
          <div className="flex items-center gap-3">
            <ProjectTile name={check.projectName} iconURL={check.iconURL} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{check.projectName}</p>
              <p className="flex flex-wrap items-center gap-1 text-xs text-neutral-medium">
                <span className="uppercase">{check.projectKey}</span>
                <span>· Current board type {check.canConvert ? 'Kanban' : 'Scrum'}</span>
                {check.canConvert && (
                  <span className="flex items-center gap-1 text-[#A78BFA]">
                    <ArrowRight size={12} />
                    Scrum
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              label="Open work items"
              value={check.openItemCount}
              note="Move to Backlog"
              noteClassName="text-[#A78BFA]"
            />
            <StatCard
              label="Completed items"
              value={check.doneItemCount}
              note="Stay as they are"
              noteClassName="text-[#34D399]"
            />
            <StatCard
              label="Team members"
              value={check.memberCount}
              note="Keep their access"
              noteClassName="text-neutral-medium"
            />
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-white">What happens when you convert</h3>
            <ul className="flex flex-col gap-2.5">
              <ChangeItem>
                <span className="font-semibold text-white">
                  {pluralize(check.openItemCount, 'open work item')}
                </span>{' '}
                move to the Backlog, unassigned to any Sprint.
              </ChangeItem>
              <ChangeItem>
                <span className="font-semibold text-white">
                  {pluralize(check.doneItemCount, 'completed item')}
                </span>{' '}
                stay as they are. They remain searchable but don't appear in the Backlog.
              </ChangeItem>
              <ChangeItem>
                <span className="font-semibold text-white">Statuses, columns, comments</span> and
                logged time are untouched.
              </ChangeItem>
              <ChangeItem isNew>
                <span className="font-semibold text-white">New:</span> Backlog, Sprints and Story
                Points (on Stories) become available.
              </ChangeItem>
            </ul>
          </div>
        </div>

        {check.canConvert ? (
          <div className="flex items-center gap-2 rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-3 py-2.5 text-xs text-[#FBBF24]">
            <Info size={14} className="flex-shrink-0" />
            Conversion is one-way. Once converted, {check.projectName} can't go back to Kanban.
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-primary-subtle bg-white/5 px-3 py-2.5 text-xs text-neutral-medium">
            <Info size={14} className="flex-shrink-0" />
            {check.projectName} is already a Scrum project.
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setProject(null)}
            className="rounded-lg border border-primary-subtle px-4 py-2 text-sm text-neutral transition-colors hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={openConfirm}
            disabled={!check.canConvert || convert.isPending}
            className="button-active rounded-lg px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {convert.isPending ? 'Converting…' : 'Convert'}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col p-6">
      <div className="max-w-[560px]">
        <h2 className="text-[17px] font-semibold text-neutral">Convert to Scrum</h2>
        <p className="mt-1 text-sm text-neutral-medium">
          Move a Kanban project to Scrum so the team can plan work in time-boxed Sprints, with a
          Backlog and Story Points.
        </p>
        <p className="mt-1 text-xs text-neutral-medium">
          Only PM Admins, Org Admins and Org Owners can convert projects.
        </p>
      </div>

      {canConvert ? (
        <div className="mt-6 flex max-w-[720px] flex-col gap-4">
          {converted && (
            <div className="flex items-center gap-3 rounded-lg border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-2.5">
              <CheckCircle2 size={18} className="flex-shrink-0 text-[#34D399]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">
                  {converted.name} is now a Scrum project
                </p>
                <p className="text-xs text-[#34D399]">
                  Create the first Sprint from the project's Backlog.
                </p>
              </div>
              <button
                type="button"
                onClick={() => toBacklog(converted.projectKey)}
                className="flex-shrink-0 rounded-lg border border-primary-subtle bg-primary-variant px-3 py-1.5 text-xs text-neutral transition-colors hover:text-white"
              >
                Open Backlog
              </button>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-neutral">Select project</label>
            <ProjectPicker value={project} onChange={setProject} />
            <p className="text-xs text-neutral-medium">
              Only Kanban projects can be converted. Projects that are already Scrum are shown
              greyed out.
            </p>
          </div>

          {renderPreview()}
        </div>
      ) : (
        <div className="mt-6 flex min-h-[200px] max-w-[720px] items-center justify-center rounded-xl border border-dashed border-primary-subtle px-6 text-center text-sm text-neutral-medium">
          You don't have permission to convert projects.
        </div>
      )}

      <PmoConvertToScrumModal
        isOpen={isModalOpen}
        stage={stage}
        projectName={check?.projectName ?? project?.name ?? ''}
        openItemCount={check?.openItemCount ?? 0}
        doneItemCount={check?.doneItemCount ?? 0}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default ConvertToScrum;
